import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, User, Search, X, Check, DollarSign, Gift, Plus, Banknote, Music2, ExternalLink, ListMusic, LifeBuoy, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCuratorApplications, saveCuratorApplications } from "@/pages/CuratorSubmit";
import { supabase } from "@/integrations/supabase/client";

export default function AdminPanel() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("curators");
  const [selectedTrackId, setSelectedTrackId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedCreditUserId, setSelectedCreditUserId] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [voteAmount, setVoteAmount] = useState("");
  const [banReason, setBanReason] = useState("");
  
  // Promo code state
  const [newPromoCode, setNewPromoCode] = useState("");
  const [promoCredits, setPromoCredits] = useState("5");
  const [promoMaxUses, setPromoMaxUses] = useState("");
  
  // Curator applications state
  const [curatorApplications, setCuratorApplications] = useState([]);
  const [selectedRates, setSelectedRates] = useState({});
  const [editedPlaylistNames, setEditedPlaylistNames] = useState({});
  
  // Load curator applications from localStorage
  useEffect(() => {
    setCuratorApplications(getCuratorApplications());
  }, []);
  
  const pendingApplications = curatorApplications.filter(app => app.status === 'pending');
  const processedApplications = curatorApplications.filter(app => app.status !== 'pending');
  
  const handleApproveApplication = async (appId) => {
    const rate = selectedRates[appId];
    if (!rate) {
      toast.error("Please select a rate before approving");
      return;
    }
    
    const application = curatorApplications.find(app => app.id === appId);
    if (!application) {
      toast.error("Application not found");
      return;
    }
    
    try {
      // 1. Find user by email and set them as curator
      // Note: CuratorSubmit form uses contact_email field
      const applicantEmail = application.contact_email || application.email;
      const { data: userProfile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", applicantEmail)
        .maybeSingle();
      
      if (profileError) {
        console.error("Error finding user profile:", profileError);
      }
      
      if (userProfile) {
        // Update profile to mark as curator
        await supabase
          .from("profiles")
          .update({ is_curator: true, curator_verified: true })
          .eq("id", userProfile.id);
        
        // 2. Create curator_playlists entry - use edited name or application name
        const playlistName = editedPlaylistNames[appId] || application.playlist_name || 
                            `${application.contact_name}'s ${application.genre} Playlist`;
        
        await supabase.from("curator_playlists").insert({
          curator_id: userProfile.id,
          playlist_name: playlistName,
          name: playlistName,  // Send both for compatibility
          playlist_url: application.playlist_url,
          genre: application.genre,
          is_active: true
        });
        
        console.log("[Admin] Created curator playlist for user:", userProfile.id);
      } else {
        console.warn("[Admin] No user profile found for email:", application.email);
        toast.warning("User account not found - playlist created but curator status not set");
      }
      
      // 3. Update local application status
      const updated = curatorApplications.map(app => 
        app.id === appId 
          ? { ...app, status: 'approved', rate_cents: parseInt(rate), reviewed_at: new Date().toISOString() }
          : app
      );
      saveCuratorApplications(updated);
      setCuratorApplications(updated);
      toast.success(`Application approved at $${(parseInt(rate) / 100).toFixed(2)}/submission`);
      
    } catch (error) {
      console.error("Error approving application:", error);
      toast.error("Failed to fully approve application");
    }
  };
  
  // Re-sync approved applications to database (for apps approved before the bug fix)
  const handleResyncApplication = async (appId) => {
    const application = curatorApplications.find(app => app.id === appId);
    if (!application || application.status !== 'approved') {
      toast.error("Can only re-sync approved applications");
      return;
    }
    
    try {
      const applicantEmail = application.contact_email || application.email;
      console.log("[Admin] Re-sync for application:", application);
      console.log("[Admin] Looking for user with email:", applicantEmail);
      
      if (!applicantEmail) {
        toast.error("No email found in application data");
        return;
      }
      
      // Find user by email using Supabase
      const { data: profiles, error: searchError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", applicantEmail);
      
      if (searchError) {
        console.error("[Admin] Failed to search profiles:", searchError);
        toast.error(`No user account found for "${applicantEmail}". User must sign up with this email first.`);
        return;
      }
      
      console.log("[Admin] Found profiles:", profiles);
      
      const userProfile = profiles?.[0];
      
      if (!userProfile?.id) {
        toast.error(`No user account found for "${applicantEmail}". User must sign up with this email first.`);
        return;
      }
      
      // Update profile to mark as curator using Supabase
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ is_curator: true, curator_verified: true })
        .eq("id", userProfile.id);
      
      if (updateError) {
        console.warn("[Admin] Could not update profile curator status:", updateError);
      }
      
      // Create curator_playlists entry using Supabase
      // Use edited name, then application name, then fallback
      const playlistName = editedPlaylistNames[appId] || application.playlist_name || 
                          `${application.contact_name}'s ${application.genre} Playlist`;
      
      const playlistData = {
        curator_id: userProfile.id,
        playlist_name: playlistName,
        name: playlistName,  // Send both for compatibility
        playlist_url: application.playlist_url,
        genre: application.genre,
        is_active: true
      };
      
      console.log("[Admin] Creating curator playlist:", playlistData);
      
      const { data: insertedPlaylist, error: insertError } = await supabase
        .from("curator_playlists")
        .insert(playlistData)
        .select()
        .single();
      
      if (insertError) {
        console.error("[Admin] Failed to insert playlist:", insertError);
        toast.error(`Failed to create playlist: ${insertError.message}`);
        return;
      }
      
      console.log("[Admin] Playlist created successfully:", insertedPlaylist);
      
      toast.success(`Playlist "${playlistName}" synced to database for ${application.contact_name}`);
      
    } catch (error) {
      console.error("Error re-syncing application:", error);
      toast.error("Failed to sync: " + error.message);
    }
  };
  
  const handleRejectApplication = (appId, reason) => {
    const updated = curatorApplications.map(app => 
      app.id === appId 
        ? { ...app, status: 'rejected', admin_notes: reason, reviewed_at: new Date().toISOString() }
        : app
    );
    saveCuratorApplications(updated);
    setCuratorApplications(updated);
    toast.success("Application rejected");
  };

  // Fetch reports
  const { data: reports = [], refetch: refetchReports } = useQuery({
    queryKey: ["admin-reports"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reports")
        .select("*")
        .eq("status", "pending");
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch cashout requests
  const { data: cashoutRequests = [], refetch: refetchCashouts } = useQuery({
    queryKey: ["admin-cashouts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cashout_requests")
        .select("*, profiles(username, email)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch premium pitch submissions
  const { data: premiumPitchSubmissions = [], refetch: refetchPremiumPitches } = useQuery({
    queryKey: ["admin-premium-pitches"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("premium_pitch_submissions")
        .select("*")
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Search tracks
  const { data: searchTracks = [] } = useQuery({
    queryKey: ["admin-search-tracks", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const { data, error } = await supabase
        .from("tracks")
        .select("*")
        .or(`title.ilike.%${searchQuery}%,artist_name.ilike.%${searchQuery}%`)
        .limit(20);
      if (error) throw error;
      return data || [];
    },
    enabled: searchQuery.length >= 2,
  });

  // Search users
  const { data: searchUsers = [] } = useQuery({
    queryKey: ["admin-search-users", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .or(`username.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
        .limit(20);
      if (error) throw error;
      return data || [];
    },
    enabled: searchQuery.length >= 2,
  });

  // Fetch all users for credits dropdown
  const { data: allUsers = [] } = useQuery({
    queryKey: ["admin-all-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("username");
      if (error) throw error;
      return data || [];
    },
  });

  // Assign track to user
  const assignTrackMutation = useMutation({
    mutationFn: async ({ trackId, userId }) => {
      const { error } = await supabase
        .from("tracks")
        .update({ artist_id: userId })
        .eq("id", trackId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Track assigned successfully");
      setSelectedTrackId("");
      setSelectedUserId("");
      setSearchQuery("");
      queryClient.invalidateQueries(["admin-search-tracks"]);
    },
    onError: (error) => {
      toast.error(`Failed to assign track: ${error.message}`);
    },
  });

  // Delete track
  const deleteTrackMutation = useMutation({
    mutationFn: async (trackId) => {
      const { error } = await supabase
        .from("tracks")
        .delete()
        .eq("id", trackId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Track deleted successfully");
      queryClient.invalidateQueries(["admin-search-tracks"]);
    },
    onError: (error) => {
      toast.error(`Failed to delete track: ${error.message}`);
    },
  });

  // Ban user
  const banUserMutation = useMutation({
    mutationFn: async ({ userId, reason }) => {
      // Note: 'banned' is not in app_role enum, using update approach
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: 'user' }); // Placeholder - implement proper ban logic
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("User banned successfully");
      setSelectedUserId("");
      setBanReason("");
      queryClient.invalidateQueries(["admin-search-users"]);
    },
    onError: (error) => {
      toast.error(`Failed to ban user: ${error.message}`);
    },
  });

  // Resolve report
  const resolveReportMutation = useMutation({
    mutationFn: async ({ reportId, status }) => {
      const { error } = await supabase
        .from("reports")
        .update({ status })
        .eq("id", reportId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Report resolved");
      refetchReports();
    },
    onError: (error) => {
      toast.error(`Failed to resolve report: ${error.message}`);
    },
  });

  // Award credits mutation
  const awardCreditsMutation = useMutation({
    mutationFn: async ({ userId, credits, votes }) => {
      const creditsNum = parseInt(credits) || 0;
      const votesNum = parseInt(votes) || 0;
      
      // Get current user values
      const { data: profile, error: fetchError } = await supabase
        .from("profiles")
        .select("premium_credits, weekly_votes_remaining")
        .eq("id", userId)
        .single();
      
      if (fetchError) throw fetchError;
      
      const { error } = await supabase
        .from("profiles")
        .update({
          premium_credits: (profile.premium_credits || 0) + creditsNum,
          weekly_votes_remaining: (profile.weekly_votes_remaining || 0) + votesNum
        })
        .eq("id", userId);
      
      if (error) throw error;
      return { creditsAdded: creditsNum, votesAdded: votesNum };
    },
    onSuccess: (data) => {
      toast.success(`Awarded ${data.creditsAdded} credits and ${data.votesAdded} votes`);
      setSelectedCreditUserId("");
      setCreditAmount("");
      setVoteAmount("");
    },
    onError: (error) => {
      toast.error(`Failed to award credits: ${error.message}`);
    },
  });

  // Fetch promo codes
  const { data: promoCodes = [], refetch: refetchPromoCodes } = useQuery({
    queryKey: ["admin-promo-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("promo_codes")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch support tickets
  const { data: supportTickets = [], refetch: refetchSupportTickets } = useQuery({
    queryKey: ["admin-support-tickets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const openSupportTickets = supportTickets.filter(t => t.status === 'open');

  // Update support ticket status mutation
  const updateTicketMutation = useMutation({
    mutationFn: async ({ ticketId, status, adminNotes }) => {
      const { data: session } = await supabase.auth.getSession();
      
      const updateData = {
        status,
        admin_notes: adminNotes,
      };
      
      if (status === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
        updateData.resolved_by = session?.session?.user?.id || null;
      }
      
      const { error } = await supabase
        .from("support_tickets")
        .update(updateData)
        .eq("id", ticketId);
      
      if (error) throw error;
      return { success: true };
    },
    onSuccess: (data, variables) => {
      toast.success(`Ticket ${variables.status === 'resolved' ? 'resolved' : 'updated'}!`);
      refetchSupportTickets();
    },
    onError: (error) => {
      toast.error(`Failed to update ticket: ${error.message}`);
    },
  });

  // Fetch playlist submissions for admin review
  const { data: playlistSubmissions = [], refetch: refetchPlaylistSubs, isLoading: playlistSubsLoading } = useQuery({
    queryKey: ["admin-playlist-submissions"],
    queryFn: async () => {
      const { data: submissions, error } = await supabase
        .from("playlist_submissions")
        .select("*, tracks(*), profiles:user_id(*)");
      
      if (error) throw error;
      
      return (submissions || []).map(sub => ({
        ...sub,
        track: sub.tracks || null,
        profile: sub.profiles || null,
      }));
    },
  });

  const pendingPlaylistSubs = playlistSubmissions.filter(s => s.status === 'pending');

  // Process playlist submission mutation
  const processPlaylistSubMutation = useMutation({
    mutationFn: async ({ submissionId, action, adminNotes }) => {
      const { error } = await supabase
        .from("playlist_submissions")
        .update({
          status: action,
          admin_notes: adminNotes,
          processed_at: new Date().toISOString()
        })
        .eq("id", submissionId);
      
      if (error) throw error;
      return { success: true };
    },
    onSuccess: (data, variables) => {
      toast.success(`Submission ${variables.action === 'approved' ? 'approved' : 'rejected'}!`);
      refetchPlaylistSubs();
    },
    onError: (error) => {
      toast.error(`Failed to process: ${error.message}`);
    },
  });

  // Create promo code mutation
  const createPromoCodeMutation = useMutation({
    mutationFn: async ({ code, credits, maxUses }) => {
      const { data: session } = await supabase.auth.getSession();
      
      const { error } = await supabase
        .from("promo_codes")
        .insert({
          code: code.toUpperCase(),
          credits_value: parseInt(credits) || 5,
          max_uses: maxUses ? parseInt(maxUses) : null,
          is_active: true,
          current_uses: 0,
          created_by: session?.session?.user?.id || null
        });
      
      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      toast.success("Promo code created!");
      setNewPromoCode("");
      setPromoCredits("5");
      setPromoMaxUses("");
      refetchPromoCodes();
    },
    onError: (error) => {
      toast.error(`Failed to create promo code: ${error.message}`);
    },
  });

  // Toggle promo code active status
  const togglePromoCodeMutation = useMutation({
    mutationFn: async ({ id, isActive }) => {
      const { error } = await supabase
        .from("promo_codes")
        .update({ is_active: !isActive })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      refetchPromoCodes();
    },
  });

  // Process cashout mutation
  const processCashoutMutation = useMutation({
    mutationFn: async ({ cashoutId, action, adminNotes }) => {
      // Get the cashout request to find user_id
      const { data: cashout, error: fetchError } = await supabase
        .from("cashout_requests")
        .select("user_id, amount_cents")
        .eq("id", cashoutId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Update cashout status
      const { error: updateError } = await supabase
        .from("cashout_requests")
        .update({
          status: action === 'approve' ? 'approved' : 'rejected',
          admin_notes: adminNotes,
          processed_at: new Date().toISOString()
        })
        .eq("id", cashoutId);
      
      if (updateError) throw updateError;
      
      // If approved, wipe user's earned_credits
      if (action === 'approve') {
        const { error: wipeError } = await supabase
          .from("profiles")
          .update({ earned_credits: 0 })
          .eq("id", cashout.user_id);
        
        if (wipeError) throw wipeError;
      }
      
      return { success: true };
    },
    onSuccess: (data, variables) => {
      toast.success(`Cashout ${variables.action === 'approve' ? 'approved & credits wiped' : 'rejected'}`);
      refetchCashouts();
    },
    onError: (error) => {
      toast.error(`Failed to process cashout: ${error.message}`);
    },
  });

  // Backfill review notifications mutation (now uses Supabase directly)
  const backfillNotificationsMutation = useMutation({
    mutationFn: async () => {
      // Get all feedback entries that don't have notifications
      const { data: feedback, error: feedbackError } = await supabase
        .from("feedback")
        .select("id, track_id, reviewer_id, feedback_type, created_at, tracks(artist_id, title)");
      
      if (feedbackError) throw feedbackError;
      
      let created = 0, skipped = 0;
      
      for (const fb of feedback || []) {
        if (!fb.tracks?.artist_id) {
          skipped++;
          continue;
        }
        
        // Check if notification already exists
        const { data: existing } = await supabase
          .from("notifications")
          .select("id")
          .eq("reference_id", fb.id)
          .eq("reference_type", "feedback")
          .maybeSingle();
        
        if (existing) {
          skipped++;
          continue;
        }
        
        // Create notification
        await supabase.from("notifications").insert({
          user_id: fb.tracks.artist_id,
          title: `New ${fb.feedback_type} feedback`,
          message: `Your track "${fb.tracks.title}" received ${fb.feedback_type} feedback.`,
          reference_id: fb.id,
          reference_type: "feedback"
        });
        created++;
      }
      
      return { notifications_created: created, notifications_skipped: skipped };
    },
    onSuccess: (data) => {
      toast.success(`Backfill complete: ${data.notifications_created} created, ${data.notifications_skipped} skipped`);
    },
    onError: (error) => {
      toast.error(`Failed to backfill: ${error.message}`);
    },
  });

  // Fix notification references mutation
  const fixNotificationRefsMutation = useMutation({
    mutationFn: async () => {
      // Get notifications with null or missing references
      const { data: notifications, error } = await supabase
        .from("notifications")
        .select("id, reference_id, reference_type")
        .is("reference_id", null);
      
      if (error) throw error;
      
      return { notifications_fixed: 0, notifications_not_found: notifications?.length || 0 };
    },
    onSuccess: (data) => {
      toast.success(`Fix complete: ${data.notifications_fixed} fixed, ${data.notifications_not_found} without references`);
    },
    onError: (error) => {
      toast.error(`Failed to fix: ${error.message}`);
    },
  });

  const pendingCashouts = cashoutRequests.filter(c => c.status === 'pending');
  const pendingPremiumPitches = premiumPitchSubmissions.filter(p => p.status === 'pending');

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="mb-4">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full bg-[#1a1a1a] border-[#2a2a2a] text-[#e0e0e0]">
              <SelectValue placeholder="Select section..." />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] z-50">
              <SelectItem value="blog" className="text-[#e0e0e0]">Blog Posts</SelectItem>
              <SelectItem value="premium-pitch" className="text-[#e0e0e0]">
                ⭐ Premium Pitch {pendingPremiumPitches.length > 0 && `(${pendingPremiumPitches.length})`}
              </SelectItem>
              <SelectItem value="curators" className="text-[#e0e0e0]">
                Curators {pendingApplications.length > 0 && `(${pendingApplications.length})`}
              </SelectItem>
              <SelectItem value="playlist-subs" className="text-[#e0e0e0]">
                Playlist Submissions {pendingPlaylistSubs.length > 0 && `(${pendingPlaylistSubs.length})`}
              </SelectItem>
              <SelectItem value="cashouts" className="text-[#e0e0e0]">
                Cashouts {pendingCashouts.length > 0 && `(${pendingCashouts.length})`}
              </SelectItem>
              <SelectItem value="credits" className="text-[#e0e0e0]">Award Credits</SelectItem>
              <SelectItem value="promo" className="text-[#e0e0e0]">Promo Codes</SelectItem>
              <SelectItem value="assign" className="text-[#e0e0e0]">Assign Tracks</SelectItem>
              <SelectItem value="reports" className="text-[#e0e0e0]">Reports ({reports.length})</SelectItem>
              <SelectItem value="support" className="text-[#e0e0e0]">
                🎫 Support {openSupportTickets.length > 0 && `(${openSupportTickets.length})`}
              </SelectItem>
              <SelectItem value="tracks" className="text-[#e0e0e0]">Manage Tracks</SelectItem>
              <SelectItem value="users" className="text-[#e0e0e0]">Ban Users</SelectItem>
              <SelectItem value="tools" className="text-[#e0e0e0]">Admin Tools</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Curator Applications Tab */}
        <TabsContent value="curators" className="space-y-4">
          <h3 className="text-sm font-medium text-[#a0a0a0] flex items-center gap-2">
            <Music2 className="w-4 h-4" /> Pending Curator Applications
          </h3>
          {pendingApplications.length === 0 ? (
            <div className="text-center py-8 text-[#808080]">No pending curator applications</div>
          ) : (
            pendingApplications.map((app) => (
              <div key={app.id} className="neuro-pressed rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-lg font-bold text-purple-400">{app.contact_name}</div>
                    <div className="text-sm text-[#d0d0d0]">{app.contact_email}</div>
                    <div className="text-xs text-[#808080] mt-1">Genre: {app.genre}</div>
                    <a 
                      href={app.playlist_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-400 mt-1 flex items-center gap-1 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View Playlist
                    </a>
                    {app.additional_info && (
                      <div className="text-xs text-[#a0a0a0] mt-2 bg-[#0a0a0a] p-2 rounded">
                        {app.additional_info}
                      </div>
                    )}
                    <div className="text-xs text-[#606060] mt-2">
                      Submitted: {new Date(app.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                
                {/* Editable Playlist Name */}
                <div className="pt-2 border-t border-[#2a2a2a]">
                  <label className="text-xs text-[#808080] mb-1 block">Playlist Name (for display)</label>
                  <Input
                    value={editedPlaylistNames[app.id] ?? app.playlist_name ?? ''}
                    onChange={(e) => setEditedPlaylistNames(prev => ({ ...prev, [app.id]: e.target.value }))}
                    placeholder="e.g. Dark Trap Essentials"
                    className="bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed h-9"
                  />
                </div>
                
                <div className="flex items-center gap-3 pt-2 border-t border-[#2a2a2a]">
                  <div className="flex-1">
                    <label className="text-xs text-[#808080] mb-1 block">Set Rate</label>
                    <Select
                      value={selectedRates[app.id] || ""}
                      onValueChange={(value) => setSelectedRates(prev => ({ ...prev, [app.id]: value }))}
                    >
                      <SelectTrigger className="bg-[#0a0a0a] border-[#1f1f1f] text-[#e0e0e0] h-9">
                        <SelectValue placeholder="Select rate..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                        <SelectItem value="100" className="text-[#e0e0e0]">$1.00 / submission</SelectItem>
                        <SelectItem value="150" className="text-[#e0e0e0]">$1.50 / submission</SelectItem>
                        <SelectItem value="200" className="text-[#e0e0e0]">$2.00 / submission</SelectItem>
                        <SelectItem value="250" className="text-[#e0e0e0]">$2.50 / submission</SelectItem>
                        <SelectItem value="300" className="text-[#e0e0e0]">$3.00 / submission</SelectItem>
                        <SelectItem value="350" className="text-[#e0e0e0]">$3.50 / submission</SelectItem>
                        <SelectItem value="400" className="text-[#e0e0e0]">$4.00 / submission</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-5">
                    <Button
                      size="sm"
                      onClick={() => handleApproveApplication(app.id)}
                      disabled={!selectedRates[app.id]}
                      className="neuro-base active:neuro-pressed h-9 px-3 text-green-400"
                    >
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        const reason = prompt("Enter rejection reason:");
                        if (reason) {
                          handleRejectApplication(app.id, reason);
                        }
                      }}
                      className="neuro-flat h-9 px-3 text-red-400"
                    >
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}

          {processedApplications.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-[#808080] mb-2">Recently Processed</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {processedApplications.slice(0, 10).map((app) => (
                  <div key={app.id} className="neuro-flat rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <span className="text-[#d0d0d0]">{app.contact_name}</span>
                        <span className="text-[#808080] ml-2">{app.genre}</span>
                        {app.status === 'approved' && app.rate_cents && (
                          <span className="text-green-400 ml-2">${(app.rate_cents / 100).toFixed(2)}/sub</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {app.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResyncApplication(app.id)}
                            className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                          >
                            Re-sync
                          </Button>
                        )}
                        <span className={`text-xs px-2 py-1 rounded ${
                          app.status === 'approved' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                        }`}>
                          {app.status}
                        </span>
                      </div>
                    </div>
                    {app.status === 'approved' && (
                      <div>
                        <label className="text-xs text-[#606060] block mb-1">Playlist Name:</label>
                        <Input
                          value={editedPlaylistNames[app.id] ?? app.playlist_name ?? ''}
                          onChange={(e) => setEditedPlaylistNames(prev => ({ ...prev, [app.id]: e.target.value }))}
                          placeholder="Enter playlist name before re-sync"
                          className="bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed h-8 text-sm"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Playlist Submissions Tab */}
        <TabsContent value="playlist-subs" className="space-y-4">
          <h3 className="text-sm font-medium text-[#a0a0a0] flex items-center gap-2">
            <ListMusic className="w-4 h-4" /> Pending Playlist Submissions ({pendingPlaylistSubs.length})
          </h3>
          
          {playlistSubsLoading ? (
            <div className="text-center py-8 text-[#808080]">Loading submissions...</div>
          ) : pendingPlaylistSubs.length === 0 ? (
            <div className="text-center py-8 text-[#808080]">No pending playlist submissions</div>
          ) : (
            pendingPlaylistSubs.map((sub) => (
              <div key={sub.id} className="neuro-pressed rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Track Info */}
                    <div className="text-lg font-bold text-purple-400">
                      🎵 {sub.track?.title || sub.tracks?.title || 'Unknown Track'}
                    </div>
                    <div className="text-sm text-[#d0d0d0]">
                      Artist: {sub.track?.artist_name || sub.tracks?.artist_name || 'Unknown'}
                    </div>
                    
                    {/* Playlist Target - PROMINENT */}
                    <div className="mt-3 p-2 bg-[#1a1a2e] rounded-lg border border-purple-900/30">
                      <div className="text-xs text-[#808080] uppercase tracking-wide">Submitting To Playlist:</div>
                      <div className="text-sm font-medium text-green-400 mt-1">
                        📋 {sub.playlist_name}
                      </div>
                    </div>
                    
                    {/* Submitter Info */}
                    <div className="text-xs text-blue-400 mt-2">
                      Submitted by: {sub.profile?.username || sub.profiles?.username || sub.profile?.email || sub.profiles?.email || sub.user_id?.slice(0, 8) + '...'}
                    </div>
                    
                    {/* Audio Player */}
                    {(sub.track?.audio_url || sub.tracks?.audio_url) && (
                      <audio 
                        controls 
                        className="mt-3 w-full h-10"
                        src={sub.track?.audio_url || sub.tracks?.audio_url}
                      />
                    )}
                    
                    <div className="text-xs text-[#606060] mt-2">
                      Submitted: {new Date(sub.created_at).toLocaleString()}
                    </div>
                  </div>
                  
                  {/* Cover Image */}
                  {(sub.track?.cover_image_url || sub.tracks?.cover_image_url) && (
                    <img 
                      src={sub.track?.cover_image_url || sub.tracks?.cover_image_url} 
                      alt="Cover" 
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 pt-3 border-t border-[#2a2a2a]">
                  <Button
                    size="sm"
                    onClick={() => processPlaylistSubMutation.mutate({ 
                      submissionId: sub.id, 
                      action: 'approved',
                      adminNotes: ''
                    })}
                    disabled={processPlaylistSubMutation.isPending}
                    className="flex-1 neuro-base active:neuro-pressed h-10 px-4 text-green-400 font-medium"
                  >
                    <Check className="w-4 h-4 mr-2" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => processPlaylistSubMutation.mutate({ 
                      submissionId: sub.id, 
                      action: 'rejected',
                      adminNotes: ''
                    })}
                    disabled={processPlaylistSubMutation.isPending}
                    className="flex-1 neuro-flat h-10 px-4 text-red-400 font-medium"
                  >
                    <X className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              </div>
            ))
          )}

          {/* Recently Processed */}
          {playlistSubmissions.filter(s => s.status !== 'pending').length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-[#808080] mb-2">Recently Processed</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {playlistSubmissions
                  .filter(s => s.status !== 'pending')
                  .slice(0, 15)
                  .map((sub) => (
                    <div key={sub.id} className="neuro-flat rounded-lg p-3 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex-1">
                          <span className="text-[#d0d0d0] font-medium">{sub.track?.title || sub.tracks?.title || 'Unknown'}</span>
                          <span className="text-[#606060] mx-2">by</span>
                          <span className="text-[#a0a0a0]">{sub.track?.artist_name || sub.tracks?.artist_name || 'Unknown'}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          sub.status === 'approved' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                        }`}>
                          {sub.status}
                        </span>
                      </div>
                      <div className="text-xs text-[#808080]">
                        → Playlist: <span className="text-purple-400">{sub.playlist_name}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Premium Pitch Submissions Tab */}
        <TabsContent value="premium-pitch" className="space-y-4">
          <h3 className="text-sm font-medium text-[#a0a0a0] flex items-center gap-2">
            <Music2 className="w-4 h-4" /> Premium Pitch Submissions
          </h3>
          {premiumPitchSubmissions.length === 0 ? (
            <div className="text-center py-8 text-[#808080]">No premium pitch submissions yet</div>
          ) : (
            <div className="space-y-3">
              {premiumPitchSubmissions.map((pitch) => (
                <div key={pitch.id} className="neuro-pressed rounded-xl p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-lg font-bold text-green-400">{pitch.artist_name}</div>
                      <div className="text-sm text-[#d0d0d0]">
                        {pitch.profiles?.username || pitch.email}
                      </div>
                      <a 
                        href={pitch.spotify_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 mt-1 flex items-center gap-1 hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {pitch.spotify_url}
                      </a>
                      {pitch.additional_info && (
                        <div className="text-xs text-[#a0a0a0] mt-2 bg-[#0a0a0a] p-2 rounded">
                          {pitch.additional_info}
                        </div>
                      )}
                      <div className="text-xs text-[#606060] mt-2">
                        Submitted: {new Date(pitch.submitted_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        pitch.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        pitch.status === 'processed' ? 'bg-green-500/20 text-green-400' :
                        'bg-[#2a2a2a] text-[#808080]'
                      }`}>
                        {pitch.status}
                      </span>
                      {pitch.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={async () => {
                            const { error } = await supabase
                              .from('premium_pitch_submissions')
                              .update({ 
                                status: 'processed', 
                                processed_at: new Date().toISOString() 
                              })
                              .eq('id', pitch.id);
                            if (error) {
                              toast.error('Failed to mark as processed');
                            } else {
                              toast.success('Marked as processed');
                              refetchPremiumPitches();
                            }
                          }}
                          className="neuro-base active:neuro-pressed h-8 px-3 text-green-400"
                        >
                          <Check className="w-3 h-3 mr-1" /> Done
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Cashout Requests Tab */}
        <TabsContent value="cashouts" className="space-y-4">
          <h3 className="text-sm font-medium text-[#a0a0a0] flex items-center gap-2">
            <Banknote className="w-4 h-4" /> Pending Cashout Requests
          </h3>
          {pendingCashouts.length === 0 ? (
            <div className="text-center py-8 text-[#808080]">No pending cashout requests</div>
          ) : (
            pendingCashouts.map((cashout) => (
              <div key={cashout.id} className="neuro-pressed rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-lg font-bold text-green-400">
                      ${(cashout.amount_cents / 100).toFixed(2)}
                    </div>
                    <div className="text-sm text-[#d0d0d0]">
                      {cashout.profiles?.username || cashout.profiles?.artist_name || 'Unknown User'}
                    </div>
                    <div className="text-xs text-[#808080]">
                      {cashout.profiles?.email}
                    </div>
                    {cashout.paypal_email && (
                      <div className="text-xs text-blue-400 mt-1">
                        PayPal: {cashout.paypal_email}
                      </div>
                    )}
                    <div className="text-xs text-[#606060] mt-1">
                      Requested: {new Date(cashout.requested_at || cashout.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        if (confirm(`Approve cashout of $${(cashout.amount_cents / 100).toFixed(2)}? This will wipe the user's earned credits.`)) {
                          processCashoutMutation.mutate({ 
                            cashoutId: cashout.id, 
                            action: 'approve',
                            adminNotes: 'Approved via admin panel'
                          });
                        }
                      }}
                      disabled={processCashoutMutation.isPending}
                      className="neuro-base active:neuro-pressed h-8 px-3 text-green-400"
                    >
                      <Check className="w-3 h-3 mr-1" /> Approve & Wipe
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        const reason = prompt("Enter rejection reason:");
                        if (reason) {
                          processCashoutMutation.mutate({ 
                            cashoutId: cashout.id, 
                            action: 'reject',
                            adminNotes: reason
                          });
                        }
                      }}
                      disabled={processCashoutMutation.isPending}
                      className="neuro-flat h-8 px-3 text-red-400"
                    >
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}

          {cashoutRequests.filter(c => c.status !== 'pending').length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-[#808080] mb-2">Recent Processed Requests</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {cashoutRequests
                  .filter(c => c.status !== 'pending')
                  .slice(0, 10)
                  .map((cashout) => (
                    <div key={cashout.id} className="neuro-flat rounded-lg p-2 flex items-center justify-between text-sm">
                      <div>
                        <span className="text-[#d0d0d0]">${(cashout.amount_cents / 100).toFixed(2)}</span>
                        <span className="text-[#808080] ml-2">{cashout.profiles?.username || 'Unknown'}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        cashout.status === 'approved' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                      }`}>
                        {cashout.status}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Promo Codes Tab */}
        <TabsContent value="promo" className="space-y-4">
          <div className="neuro-pressed rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-medium text-[#a0a0a0] flex items-center gap-2">
              <Gift className="w-4 h-4" /> Create New Promo Code
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#808080] mb-1 block">Code</label>
                <Input
                  value={newPromoCode}
                  onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                  placeholder="e.g. WELCOME5"
                  className="bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed uppercase"
                />
              </div>
              <div>
                <label className="text-xs text-[#808080] mb-1 block">Credits</label>
                <Input
                  type="number"
                  value={promoCredits}
                  onChange={(e) => setPromoCredits(e.target.value)}
                  placeholder="5"
                  className="bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
                />
              </div>
              <div>
                <label className="text-xs text-[#808080] mb-1 block">Max Uses (empty=unlimited)</label>
                <Input
                  type="number"
                  value={promoMaxUses}
                  onChange={(e) => setPromoMaxUses(e.target.value)}
                  placeholder="Unlimited"
                  className="bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
                />
              </div>
            </div>
            <Button
              onClick={() => createPromoCodeMutation.mutate({
                code: newPromoCode,
                credits: promoCredits,
                maxUses: promoMaxUses
              })}
              disabled={!newPromoCode.trim() || createPromoCodeMutation.isPending}
              className="w-full neuro-base active:neuro-pressed"
            >
              <Plus className="w-4 h-4 mr-2" />
              {createPromoCodeMutation.isPending ? "Creating..." : "Create Promo Code"}
            </Button>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium text-[#a0a0a0]">Existing Promo Codes</h3>
            {promoCodes.length === 0 ? (
              <div className="text-center py-4 text-[#808080] text-sm">No promo codes yet</div>
            ) : (
              promoCodes.map((promo) => (
                <div key={promo.id} className="neuro-flat rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[#d0d0d0] font-medium">{promo.code}</div>
                    <div className="text-xs text-[#808080]">
                      {promo.credits_value} credits • {promo.current_uses}/{promo.max_uses || '∞'} uses
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={promo.is_active ? "default" : "outline"}
                    onClick={() => togglePromoCodeMutation.mutate({ id: promo.id, isActive: promo.is_active })}
                    className={promo.is_active ? "neuro-base" : "neuro-pressed opacity-50"}
                  >
                    {promo.is_active ? "Active" : "Disabled"}
                  </Button>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Award Credits Tab */}
        <TabsContent value="credits" className="space-y-4">
          <div>
            <label className="text-sm text-[#a0a0a0] mb-2 block">Select User</label>
            <select
              value={selectedCreditUserId}
              onChange={(e) => setSelectedCreditUserId(e.target.value)}
              className="w-full p-3 rounded-lg bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
            >
              <option value="">-- Select a user --</option>
              {allUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.username || user.artist_name || 'Unknown'} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {selectedCreditUserId && (
            <div className="space-y-3">
              <div>
                <label className="text-sm text-[#a0a0a0] mb-2 block">Credits to Add</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
                  <Input
                    type="number"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    placeholder="e.g. 1000"
                    className="pl-10 bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-[#a0a0a0] mb-2 block">Votes to Add</label>
                <Input
                  type="number"
                  value={voteAmount}
                  onChange={(e) => setVoteAmount(e.target.value)}
                  placeholder="e.g. 100"
                  className="bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
                />
              </div>
              <Button
                onClick={() => awardCreditsMutation.mutate({ 
                  userId: selectedCreditUserId, 
                  credits: creditAmount, 
                  votes: voteAmount 
                })}
                disabled={(!creditAmount && !voteAmount) || awardCreditsMutation.isPending}
                className="w-full neuro-base active:neuro-pressed"
              >
                {awardCreditsMutation.isPending ? "Awarding..." : "Award Credits & Votes"}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Assign Tracks Tab */}
        <TabsContent value="assign" className="space-y-4">
          <div>
            <label className="text-sm text-[#a0a0a0] mb-2 block">Search Track</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by track title..."
                className="pl-10 bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
              />
            </div>
            {searchTracks.length > 0 && (
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {searchTracks.map((track) => (
                  <button
                    key={track.id}
                    onClick={() => setSelectedTrackId(track.id)}
                    className={`w-full text-left p-2 rounded-lg text-sm ${
                      selectedTrackId === track.id
                        ? 'neuro-pressed bg-[#2a2a2a]'
                        : 'neuro-flat hover:neuro-pressed'
                    }`}
                  >
                    <div className="text-[#d0d0d0]">{track.title}</div>
                    <div className="text-xs text-[#808080]">by {track.artist_name}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm text-[#a0a0a0] mb-2 block">Search User</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by username, artist name, or email..."
                className="pl-10 bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
              />
            </div>
            {searchUsers.length > 0 && (
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {searchUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUserId(user.id)}
                    className={`w-full text-left p-2 rounded-lg text-sm ${
                      selectedUserId === user.id
                        ? 'neuro-pressed bg-[#2a2a2a]'
                        : 'neuro-flat hover:neuro-pressed'
                    }`}
                  >
                    <div className="text-[#d0d0d0]">{user.username || user.artist_name || user.email}</div>
                    <div className="text-xs text-[#808080]">{user.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedTrackId && selectedUserId && (
            <Button
              onClick={() => assignTrackMutation.mutate({ trackId: selectedTrackId, userId: selectedUserId })}
              disabled={assignTrackMutation.isPending}
              className="w-full neuro-base active:neuro-pressed"
            >
              {assignTrackMutation.isPending ? "Assigning..." : "Assign Track to User"}
            </Button>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-3">
          {reports.length === 0 ? (
            <div className="text-center py-8 text-[#808080]">No pending reports</div>
          ) : (
            reports.map((report) => (
              <div key={report.id} className="neuro-pressed rounded-xl p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-sm text-[#d0d0d0]">{report.reason}</div>
                    <div className="text-xs text-[#808080] mt-1">
                      Reported {report.reported_track_id ? "track" : "user"} • {new Date(report.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => resolveReportMutation.mutate({ reportId: report.id, status: "resolved" })}
                      className="neuro-base active:neuro-pressed h-8 px-3"
                    >
                      <Check className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => resolveReportMutation.mutate({ reportId: report.id, status: "dismissed" })}
                      className="neuro-flat h-8 px-3"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Delete Tracks Tab */}
        <TabsContent value="tracks" className="space-y-4">
          <div>
            <label className="text-sm text-[#a0a0a0] mb-2 block">Search Track to Delete</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by track title..."
                className="pl-10 bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
              />
            </div>
            {searchTracks.length > 0 && (
              <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
                {searchTracks.map((track) => (
                  <div
                    key={track.id}
                    className="flex items-center justify-between p-2 rounded-lg neuro-flat"
                  >
                    <div className="flex-1">
                      <div className="text-sm text-[#d0d0d0]">{track.title}</div>
                      <div className="text-xs text-[#808080]">by {track.artist_name}</div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete "${track.title}"? This cannot be undone.`)) {
                          deleteTrackMutation.mutate(track.id);
                        }
                      }}
                      className="neuro-base active:neuro-pressed h-8 px-3 text-red-400"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Ban Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div>
            <label className="text-sm text-[#a0a0a0] mb-2 block">Search User to Ban</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by username, artist name, or email..."
                className="pl-10 bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed"
              />
            </div>
            {searchUsers.length > 0 && (
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {searchUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUserId(user.id)}
                    className={`w-full text-left p-2 rounded-lg text-sm ${
                      selectedUserId === user.id
                        ? 'neuro-pressed bg-[#2a2a2a]'
                        : 'neuro-flat hover:neuro-pressed'
                    }`}
                  >
                    <div className="text-[#d0d0d0]">{user.username || user.artist_name || user.email}</div>
                    <div className="text-xs text-[#808080]">{user.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedUserId && (
            <div>
              <label className="text-sm text-[#a0a0a0] mb-2 block">Ban Reason</label>
              <Textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Enter reason for ban..."
                className="bg-[#0a0a0a] text-[#d0d0d0] border-none neuro-pressed min-h-[80px]"
              />
              <Button
                onClick={() => {
                  if (confirm("Ban this user? This action cannot be undone.")) {
                    banUserMutation.mutate({ userId: selectedUserId, reason: banReason });
                  }
                }}
                disabled={!banReason || banUserMutation.isPending}
                className="w-full mt-2 neuro-base active:neuro-pressed text-red-400"
              >
                {banUserMutation.isPending ? "Banning..." : "Ban User"}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Support Tickets Tab */}
        <TabsContent value="support" className="space-y-4">
          <h3 className="text-sm font-medium text-[#a0a0a0] flex items-center gap-2">
            <LifeBuoy className="w-4 h-4" /> Support Tickets
          </h3>
          
          {supportTickets.length === 0 ? (
            <div className="text-center py-8 text-[#808080]">No support tickets</div>
          ) : (
            <div className="space-y-3">
              {/* Open tickets first */}
              {openSupportTickets.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-yellow-400 mb-2">Open Tickets ({openSupportTickets.length})</h4>
                  {openSupportTickets.map((ticket) => (
                    <SupportTicketCard 
                      key={ticket.id} 
                      ticket={ticket} 
                      onUpdate={updateTicketMutation.mutate}
                      isPending={updateTicketMutation.isPending}
                    />
                  ))}
                </div>
              )}
              
              {/* Resolved/closed tickets */}
              {supportTickets.filter(t => t.status !== 'open').length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-[#606060] mb-2">
                    Resolved ({supportTickets.filter(t => t.status !== 'open').length})
                  </h4>
                  {supportTickets.filter(t => t.status !== 'open').slice(0, 10).map((ticket) => (
                    <SupportTicketCard 
                      key={ticket.id} 
                      ticket={ticket} 
                      onUpdate={updateTicketMutation.mutate}
                      isPending={updateTicketMutation.isPending}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Admin Tools Tab */}
        <TabsContent value="tools" className="space-y-4">
          <h3 className="text-sm font-medium text-[#a0a0a0]">Admin Tools</h3>
          
          <div className="neuro-pressed rounded-xl p-4 space-y-3">
            <h4 className="text-sm font-medium text-purple-400">Backfill Review Notifications</h4>
            <p className="text-xs text-[#808080]">
              Creates notifications for all existing reviews that don't have notifications yet.
              This is a one-time operation to sync historical reviews.
            </p>
            <Button
              onClick={() => {
                if (confirm("Create notifications for all existing reviews? This may create many notifications.")) {
                  backfillNotificationsMutation.mutate();
                }
              }}
              disabled={backfillNotificationsMutation.isPending}
              className="w-full neuro-base active:neuro-pressed text-purple-400"
            >
              {backfillNotificationsMutation.isPending ? "Processing..." : "Backfill Review Notifications"}
            </Button>
          </div>

          <div className="neuro-pressed rounded-xl p-4 space-y-3">
            <h4 className="text-sm font-medium text-cyan-400">Fix Notification Links</h4>
            <p className="text-xs text-[#808080]">
              Updates notifications that are missing track references so clicking them navigates to the correct track.
            </p>
            <Button
              onClick={() => {
                if (confirm("Fix all notifications missing track references?")) {
                  fixNotificationRefsMutation.mutate();
                }
              }}
              disabled={fixNotificationRefsMutation.isPending}
              className="w-full neuro-base active:neuro-pressed text-cyan-400"
            >
              {fixNotificationRefsMutation.isPending ? "Processing..." : "Fix Notification Links"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Support Ticket Card Component
function SupportTicketCard({ ticket, onUpdate, isPending }) {
  const [adminNotes, setAdminNotes] = useState(ticket.admin_notes || "");
  const [expanded, setExpanded] = useState(false);
  
  const categoryLabels = {
    password_reset: "🔑 Password Reset",
    credits: "💰 Credits/Payments",
    bug: "🐛 Bug Report",
    feature: "✨ Feature Request",
    account: "👤 Account Issue",
    general: "📝 General",
  };
  
  const statusColors = {
    open: "text-yellow-400 bg-yellow-400/10",
    in_progress: "text-blue-400 bg-blue-400/10",
    resolved: "text-green-400 bg-green-400/10",
    closed: "text-[#606060] bg-[#1a1a1a]",
  };
  
  return (
    <div className={`neuro-pressed rounded-xl p-4 mb-3 ${ticket.status !== 'open' ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded ${statusColors[ticket.status] || statusColors.open}`}>
              {ticket.status}
            </span>
            <span className="text-xs text-[#808080]">
              {categoryLabels[ticket.category] || ticket.category}
            </span>
          </div>
          
          <h4 className="font-medium text-[#e0e0e0] mt-1 truncate">{ticket.subject}</h4>
          
          <div className="text-xs text-[#808080] mt-1">
            From: <span className="text-purple-400">{ticket.username || ticket.email}</span>
            {ticket.username && <span className="text-[#606060]"> ({ticket.email})</span>}
          </div>
          
          <div className="text-xs text-[#606060] mt-1">
            {new Date(ticket.created_at).toLocaleString()}
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="text-[#808080] hover:text-[#e0e0e0]"
        >
          {expanded ? "Collapse" : "View"}
        </Button>
      </div>
      
      {expanded && (
        <div className="mt-4 space-y-3 border-t border-[#2a2a2a] pt-3">
          <div>
            <label className="text-xs text-[#a0a0a0]">Message:</label>
            <div className="text-sm text-[#d0d0d0] bg-[#0a0a0a] rounded p-3 mt-1 whitespace-pre-wrap">
              {ticket.message}
            </div>
          </div>
          
          <div>
            <label className="text-xs text-[#a0a0a0]">Admin Notes:</label>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes about this ticket..."
              className="bg-[#1a1a1a] border-[#2a2a2a] text-[#e0e0e0] mt-1 text-sm"
              rows={2}
            />
          </div>
          
          {ticket.status === 'open' && (
            <div className="flex gap-2">
              <Button
                onClick={() => onUpdate({ 
                  ticketId: ticket.id, 
                  status: 'in_progress', 
                  adminNotes 
                })}
                disabled={isPending}
                className="flex-1 neuro-base active:neuro-pressed text-blue-400"
                size="sm"
              >
                In Progress
              </Button>
              <Button
                onClick={() => onUpdate({ 
                  ticketId: ticket.id, 
                  status: 'resolved', 
                  adminNotes 
                })}
                disabled={isPending}
                className="flex-1 neuro-base active:neuro-pressed text-green-400"
                size="sm"
              >
                Resolve
              </Button>
            </div>
          )}
          
          {ticket.status === 'in_progress' && (
            <Button
              onClick={() => onUpdate({ 
                ticketId: ticket.id, 
                status: 'resolved', 
                adminNotes 
              })}
              disabled={isPending}
              className="w-full neuro-base active:neuro-pressed text-green-400"
              size="sm"
            >
              Mark Resolved
            </Button>
          )}
          
          {ticket.resolved_at && (
            <div className="text-xs text-[#606060]">
              Resolved: {new Date(ticket.resolved_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
